/**
 * connection-quality.js — Phase 1
 *
 * Optional, opt-in client-side network-quality monitor for VibeLive.
 *
 * Samples RTCPeerConnection.getStats() on each active upstream + downstream
 * peer connection on a fixed cadence, derives normalized metrics, classifies
 * into discrete quality levels with hysteresis to prevent flapping, and emits
 * transition events for:
 *   - Overall quality   (your perspective on the call as a whole)
 *   - Per-remote-member (one snapshot per remote member you receive)
 *
 * Phase 1 is local-only: every signal is measurable from *your* browser.
 * It cannot observe what remote peers see of your stream. That arrives in Phase 2.
 *
 * Usage (driven by vibelive-core when the feature flag is on):
 *   const monitor = createMonitor({
 *       getPeerConnections: () => bridge.getAllActivePeerConnections(),
 *       localMemberId: '...',
 *       onLevelChange: (snapshot) => { ... },
 *       onMemberLevelChange: (memberId, snapshot) => { ... },
 *   });
 *   monitor.start();
 *   // ... later
 *   monitor.stop();
 *
 * Public methods on the returned monitor:
 *   - start(): begin sampling
 *   - stop(): stop sampling and clear timers
 *   - getSnapshot(): current overall snapshot
 *   - getMemberSnapshot(memberId): current per-member snapshot
 *
 * Snapshot shape:
 *   {
 *       level: 'good' | 'fair' | 'poor' | 'lost',
 *       reason: string | null,           // why we're at this level (see REASONS)
 *       metrics: {
 *           rttMs: number | null,        // candidate-pair RTT in ms
 *           packetLossPct: number | null,// 0..100, rolling window
 *           jitterMs: number | null,
 *           outboundKbps: number | null, // upstream only
 *           freezeRate: number | null,   // freezes per minute, video only
 *           bytesGrowing: boolean | null,// stall detection
 *       },
 *       sampledAt: number                // ms timestamp
 *   }
 */

// ──────────────────────────────────────────────────────────────────────────
// Defaults — all thresholds tunable via opts.thresholds when constructing.
// ──────────────────────────────────────────────────────────────────────────

const DEFAULT_THRESHOLDS = {
    // Bumped from {fair:250, poor:500, lost:1000}. Real-world mobile/wifi/transatlantic
    // baselines routinely sit 200–350 ms with no perceptible video degradation, and a
    // persistent "High latency" banner on otherwise-fine video is worse UX than letting
    // the RTT band run a bit higher. New band:
    //   good <400 ms, fair 400–800 ms, poor 800–1500 ms, lost ≥1500 ms.
    rttMs:         { fair: 400,  poor: 800,  lost: 1500 },
    packetLossPct: { fair: 3,    poor: 6,    lost: 10  },
    jitterMs:      { fair: 30,   poor: 60,   lost: 200 },
    // bytesReceived flat for this many ms while audio/video tracks are healthy → 'lost'
    stallMs:       8000,
};

const DEFAULT_OPTS = {
    sampleIntervalMs:    2000,    // how often to call getStats() per PC
    degradeSamplesNeeded: 2,      // consecutive degraded samples before dropping a level
    recoverSamplesNeeded: 3,      // consecutive good samples before improving a level
};

// Quality levels ordered from best to worst.
const LEVELS = ['good', 'fair', 'poor', 'lost'];
const levelRank = (l) => LEVELS.indexOf(l);

// Reason vocabulary — embedders may pattern-match on these.
export const REASONS = {
    RTT:           'rtt',
    JITTER:        'jitter',
    AUDIO_LOSS:    'audio-loss',
    VIDEO_LOSS:    'video-loss',
    VIDEO_FREEZE:  'video-freeze',
    UPSTREAM_LOSS: 'upstream-loss',
    BITRATE_LOW:   'bitrate-low',
    STALLED:       'stalled',
    OFFLINE:       'offline',
    RECOVERED:     'recovered',
};

// ──────────────────────────────────────────────────────────────────────────
// Public factory
// ──────────────────────────────────────────────────────────────────────────

/**
 * Create a connection-quality monitor instance. Stateless until start() is called.
 * Pass localMemberId so we can correctly skip the local member when classifying
 * "per-remote-member" snapshots (the local upstream feeds *overall* quality only).
 *
 * @param {Object} opts
 * @param {() => Array} opts.getPeerConnections - returns active PCs (see bridge.getAllActivePeerConnections)
 * @param {string} [opts.localMemberId]
 * @param {(snapshot) => void} [opts.onLevelChange]
 * @param {(memberId, snapshot) => void} [opts.onMemberLevelChange]
 * @param {number} [opts.sampleIntervalMs]
 * @param {Object} [opts.thresholds]
 * @param {number} [opts.degradeSamplesNeeded]
 * @param {number} [opts.recoverSamplesNeeded]
 */
export function createMonitor(opts = {}) {
    // Deep-merge thresholds one level deep so callers can override just `rttMs.fair`
    // without wiping the rest of the rttMs map.
    const userT = opts.thresholds || {};
    const mergedThresholds = {
        rttMs:         { ...DEFAULT_THRESHOLDS.rttMs,         ...(userT.rttMs         || {}) },
        packetLossPct: { ...DEFAULT_THRESHOLDS.packetLossPct, ...(userT.packetLossPct || {}) },
        jitterMs:      { ...DEFAULT_THRESHOLDS.jitterMs,      ...(userT.jitterMs      || {}) },
        stallMs:       (typeof userT.stallMs === 'number') ? userT.stallMs : DEFAULT_THRESHOLDS.stallMs,
    };
    const cfg = {
        ...DEFAULT_OPTS,
        ...opts,
        thresholds: mergedThresholds,
    };

    // ── Internal state ────────────────────────────────────────────────────
    let timer = null;
    let running = false;

    // Per-PC last-sample cache for delta computation:
    //   key: `${memberId}|${streamType}|${direction}`
    //   val: { ts, packetsLost, packetsReceived, bytesReceived, bytesSent, totalFreezesDuration }
    const pcCache = new Map();

    // Per-member current classified snapshot (remote members only).
    // Also a transition counter that drives hysteresis.
    //   key: memberId
    //   val: { snapshot, pendingLevel, sameLevelStreak }
    const memberState = new Map();

    // Overall (your side) snapshot + hysteresis state. Folds upstream WITH the worst
    // remote downstream — this is what the user-facing onConnectionQuality callback gets.
    let overallState = {
        snapshot: makeEmptySnapshot(),
        pendingLevel: 'good',
        sameLevelStreak: 0,
    };

    // Local-upstream-only snapshot + hysteresis state. Driven purely by *your* outbound
    // PCs (no fold with remote downstreams). This is what the self-tile banner should
    // use, so a remote peer's lossy downstream cannot show up as "High latency" on the
    // local camera tile.
    let localUpstreamState = {
        snapshot: makeEmptySnapshot(),
        pendingLevel: 'good',
        sameLevelStreak: 0,
    };

    // ── Public API ────────────────────────────────────────────────────────

    function start() {
        if (running) return;
        running = true;
        // Fire an initial sample immediately so the embedder has a snapshot.
        sampleOnce().catch(err => console.debug('[ConnectionQuality] initial sample error:', err));
        timer = setInterval(() => {
            sampleOnce().catch(err => console.debug('[ConnectionQuality] sample error:', err));
        }, cfg.sampleIntervalMs);
    }

    // True while the OS reports no network. Flips the disappeared-member reason
    // to 'offline' and is set/cleared by signalOffline/signalOnline.
    let isOffline = false;

    function stop() {
        running = false;
        isOffline = false;
        if (timer) { clearInterval(timer); timer = null; }
        pcCache.clear();
        memberState.clear();
        overallState = { snapshot: makeEmptySnapshot(), pendingLevel: 'good', sameLevelStreak: 0 };
        localUpstreamState = { snapshot: makeEmptySnapshot(), pendingLevel: 'good', sameLevelStreak: 0 };
    }

    /**
     * Immediately push level:'lost', reason:'offline' to every member tile and
     * the local tile. Uses getKnownMemberIds (tileRegistry) as the source so
     * the signal fires even before the first 2 s sample tick.
     */
    function signalOffline() {
        isOffline = true;
        const now = Date.now();
        const snap = { level: 'lost', reason: REASONS.OFFLINE, metrics: {}, sampledAt: now };
        const state = { snapshot: snap, pendingLevel: 'lost', sameLevelStreak: 0 };

        const ids = cfg.getKnownMemberIds ? cfg.getKnownMemberIds() : [];
        if (cfg.onMemberLevelChange) {
            for (const memberId of ids) {
                memberState.set(memberId, state);
                try { cfg.onMemberLevelChange(memberId, { ...snap, prevLevel: null }); }
                catch (e) { console.debug('[ConnectionQuality] signalOffline threw:', e); }
            }
        }
        if (cfg.onLocalLevelChange) {
            localUpstreamState = state;
            try { cfg.onLocalLevelChange({ ...snap, prevLevel: null }); }
            catch (e) { console.debug('[ConnectionQuality] signalOffline threw:', e); }
        }
    }

    /** Clear the offline flag so normal classification resumes on the next tick. */
    function signalOnline() {
        isOffline = false;
    }

    function getSnapshot() { return { ...overallState.snapshot }; }
    function getLocalSnapshot() { return { ...localUpstreamState.snapshot }; }
    function getMemberSnapshot(memberId) {
        const s = memberState.get(memberId);
        return s ? { ...s.snapshot } : null;
    }

    // ── Sampling loop ────────────────────────────────────────────────────

    async function sampleOnce() {
        if (!running || isOffline) return;
        const pcs = (cfg.getPeerConnections && cfg.getPeerConnections()) || [];
        const now = Date.now();

        // Per-member aggregations from this tick: worst-case across that member's PCs.
        // Map<memberId, {metrics, reasons}>
        const perMember = new Map();
        // Upstream aggregation (the local side feeds overall quality).
        let upstream = null;

        for (const pc of pcs) {
            const rec = await samplePeer(pc, now);
            if (!rec) continue;

            if (pc.direction === 'up') {
                upstream = mergeWorst(upstream, rec);
            } else {
                // remote, downstream — attribute to that member
                if (cfg.localMemberId && pc.memberId === cfg.localMemberId) continue;
                // Quality bar is rendered on the camera tile only (see VibeLiveCore
                // #renderQualityBar — it targets tileRegistry[memberId].camera). A
                // stalled screenshare downstream (common: peer ended share but Galene
                // hasn't closed the down PC, or video-only PC with replaceTrack(null))
                // would otherwise poison the camera tile via mergeWorst and pin the
                // bar to "No data — reconnecting…" indefinitely. Filter to camera.
                if (pc.streamType !== 'camera') continue;
                const existing = perMember.get(pc.memberId);
                perMember.set(pc.memberId, mergeWorst(existing, rec));
            }
        }

        // ── Per-member classification & event dispatch ─────────────────────
        for (const [memberId, rec] of perMember.entries()) {
            const classified = classifyDownstream(rec, cfg.thresholds);
            const snap = {
                level: classified.level,
                reason: classified.reason,
                metrics: rec.metrics,
                sampledAt: now,
            };
            const cur = memberState.get(memberId);
            // Fast-clear: if the freeze banner is showing but this sample has no freeze,
            // bypass hysteresis and clear immediately instead of waiting recoverSamplesNeeded.
            const recoverNeeded = (cur?.snapshot?.reason === REASONS.VIDEO_FREEZE && classified.reason !== REASONS.VIDEO_FREEZE)
                ? 1
                : cfg.recoverSamplesNeeded;
            const transition = applyHysteresis(cur, snap, cfg.degradeSamplesNeeded, recoverNeeded);
            memberState.set(memberId, transition.state);
            if (transition.changed && cfg.onMemberLevelChange) {
                try { cfg.onMemberLevelChange(memberId, { ...transition.state.snapshot, prevLevel: transition.prevLevel }); }
                catch (e) { console.debug('[ConnectionQuality] onMemberLevelChange threw:', e); }
            }
        }

        // Clean up members that have disappeared (no PCs this tick) — fire 'lost' once.
        // While offline use reason:'offline' so the tile reads "Network unavailable".
        for (const memberId of [...memberState.keys()]) {
            if (perMember.has(memberId)) continue;
            const cur = memberState.get(memberId);
            if (!cur || cur.snapshot.level === 'lost') continue;
            const reason = isOffline ? REASONS.OFFLINE : REASONS.STALLED;
            const lostSnap = { level: 'lost', reason, metrics: cur.snapshot.metrics, sampledAt: now };
            const transition = applyHysteresis(cur, lostSnap, 1, cfg.recoverSamplesNeeded);
            memberState.set(memberId, transition.state);
            if (transition.changed && cfg.onMemberLevelChange) {
                try { cfg.onMemberLevelChange(memberId, { ...transition.state.snapshot, prevLevel: transition.prevLevel }); }
                catch (e) { console.debug('[ConnectionQuality] onMemberLevelChange threw:', e); }
            }
        }

        // ── Local-upstream-only classification: drives the self-tile banner ──
        // Uses ONLY your outbound PC(s). A remote peer's flaky downstream cannot
        // poison this snapshot, so the self-tile never gets blamed for someone else's
        // network. This is what onLocalLevelChange fires for.
        const localSnap = upstream
            ? { ...classifyOverall(upstream, cfg.thresholds), metrics: upstream.metrics, sampledAt: now }
            : { level: 'good', reason: null, metrics: emptyMetrics(), sampledAt: now };
        const localTrans = applyHysteresis(localUpstreamState, localSnap, cfg.degradeSamplesNeeded, cfg.recoverSamplesNeeded);
        localUpstreamState = localTrans.state;
        if (localTrans.changed && cfg.onLocalLevelChange) {
            try { cfg.onLocalLevelChange({ ...localTrans.state.snapshot, prevLevel: localTrans.prevLevel }); }
            catch (e) { console.debug('[ConnectionQuality] onLocalLevelChange threw:', e); }
        }

        // ── Overall classification: worst of upstream-direct + your-link-to-SFU ──
        // "Overall" folds in the worst remote downstream — if everything from the SFU
        // is broken, your overall network picture is too. Embedders consuming
        // onConnectionQuality / getSnapshot() see this; it is NOT used for the local
        // self-tile banner (that uses the upstream-only snapshot above).
        let overallRec = upstream;
        for (const rec of perMember.values()) {
            overallRec = mergeWorst(overallRec, rec);
        }
        const overallSnap = overallRec
            ? { ...classifyOverall(overallRec, cfg.thresholds), metrics: overallRec.metrics, sampledAt: now }
            : { level: 'good', reason: null, metrics: emptyMetrics(), sampledAt: now };
        const overallTrans = applyHysteresis(overallState, overallSnap, cfg.degradeSamplesNeeded, cfg.recoverSamplesNeeded);
        overallState = overallTrans.state;
        if (overallTrans.changed && cfg.onLevelChange) {
            try { cfg.onLevelChange({ ...overallTrans.state.snapshot, prevLevel: overallTrans.prevLevel }); }
            catch (e) { console.debug('[ConnectionQuality] onLevelChange threw:', e); }
        }
    }

    // ── Per-PC sampling ──────────────────────────────────────────────────

    async function samplePeer(pcRec, now) {
        const { pc, memberId, streamType, direction } = pcRec;
        if (!pc || typeof pc.getStats !== 'function') return null;
        const cacheKey = `${memberId}|${streamType}|${direction}`;
        let stats;
        try { stats = await pc.getStats(); }
        catch (_) { return null; }

        // Aggregate raw values from one getStats() call across all relevant report types.
        let rttMs = null;
        let jitterMs = null;
        let packetsLost = 0;
        let packetsReceived = 0;
        let packetsLostUp = 0;       // upstream-reported loss
        let packetsSent = 0;
        let bytesReceived = 0;
        let bytesSent = 0;
        let totalFreezesDuration = 0; // cumulative ms of frozen video (WebRTC spec: freeze = ≥150ms stall)
        // We accumulate audio vs video separately for finer reason attribution.
        let audioLost = 0, audioRecv = 0;
        let videoLost = 0, videoRecv = 0;

        stats.forEach((r) => {
            if (r.type === 'candidate-pair' && r.nominated && typeof r.currentRoundTripTime === 'number') {
                rttMs = Math.round(r.currentRoundTripTime * 1000);
            }
            if (r.type === 'remote-inbound-rtp' && typeof r.roundTripTime === 'number') {
                // Upstream-side RTT from SFU back to us.
                const rt = Math.round(r.roundTripTime * 1000);
                if (rttMs === null || rt > rttMs) rttMs = rt;
                if (typeof r.packetsLost === 'number') packetsLostUp += r.packetsLost;
            }
            if (r.type === 'inbound-rtp') {
                if (typeof r.packetsLost === 'number') packetsLost += r.packetsLost;
                if (typeof r.packetsReceived === 'number') packetsReceived += r.packetsReceived;
                if (typeof r.jitter === 'number') {
                    const j = r.jitter * 1000;
                    if (jitterMs === null || j > jitterMs) jitterMs = j;
                }
                if (typeof r.bytesReceived === 'number') bytesReceived += r.bytesReceived;
                if (r.kind === 'video' && typeof r.totalFreezesDuration === 'number') totalFreezesDuration += r.totalFreezesDuration * 1000; // spec gives seconds, convert to ms
                if (r.kind === 'audio') {
                    if (typeof r.packetsLost === 'number') audioLost += r.packetsLost;
                    if (typeof r.packetsReceived === 'number') audioRecv += r.packetsReceived;
                }
                if (r.kind === 'video') {
                    if (typeof r.packetsLost === 'number') videoLost += r.packetsLost;
                    if (typeof r.packetsReceived === 'number') videoRecv += r.packetsReceived;
                }
            }
            if (r.type === 'outbound-rtp') {
                if (typeof r.packetsSent === 'number') packetsSent += r.packetsSent;
                if (typeof r.bytesSent === 'number') bytesSent += r.bytesSent;
            }
        });

        // Compute deltas vs previous sample for this PC.
        let prev = pcCache.get(cacheKey);

        // Counter-reset detection: WebRTC cumulative counters (bytesReceived/Sent,
        // packets*, totalFreezesDuration) monotonically increase for the lifetime of a single
        // RTCPeerConnection. If ANY of them drops below the previous sample, the
        // underlying PC was rebuilt (ICE restart, track replacement, reconnect) while
        // we kept the same cache key (memberId|streamType|direction). Treat this tick
        // as a fresh baseline so we don't (a) compute negative deltas that get clamped
        // to 0 and (b) carry a stale `flatSince` forward and stay stuck in 'lost'.
        if (prev && (
            bytesReceived < prev.bytesReceived ||
            bytesSent     < prev.bytesSent     ||
            packetsLost   < prev.packetsLost   ||
            packetsReceived < prev.packetsReceived ||
            audioLost  < prev.audioLost  ||
            audioRecv  < prev.audioRecv  ||
            videoLost  < prev.videoLost  ||
            videoRecv  < prev.videoRecv  ||
            packetsLostUp < prev.packetsLostUp ||
            packetsSent   < prev.packetsSent   ||
            totalFreezesDuration < prev.totalFreezesDuration
        )) {
            // Re-baseline: drop the stale row and treat this sample as the first one
            // for the new PC. The classifier sees `hadPrev=false` and returns 'good',
            // which lets hysteresis recover the member out of 'lost'.
            pcCache.delete(cacheKey);
            prev = undefined;
        }

        const dt = prev ? Math.max(1, now - prev.ts) : 0;

        const deltaPacketsLost = prev ? Math.max(0, packetsLost - prev.packetsLost) : 0;
        const deltaPacketsRecv = prev ? Math.max(0, packetsReceived - prev.packetsReceived) : 0;
        const deltaAudioLost   = prev ? Math.max(0, audioLost - prev.audioLost) : 0;
        const deltaAudioRecv   = prev ? Math.max(0, audioRecv - prev.audioRecv) : 0;
        const deltaVideoLost   = prev ? Math.max(0, videoLost - prev.videoLost) : 0;
        const deltaVideoRecv   = prev ? Math.max(0, videoRecv - prev.videoRecv) : 0;
        const deltaPacketsLostUp = prev ? Math.max(0, packetsLostUp - prev.packetsLostUp) : 0;
        const deltaPacketsSent   = prev ? Math.max(0, packetsSent - prev.packetsSent) : 0;
        const deltaBytesReceived = prev ? Math.max(0, bytesReceived - prev.bytesReceived) : 0;
        const deltaBytesSent     = prev ? Math.max(0, bytesSent - prev.bytesSent) : 0;
        const deltaFreezesDurationMs = prev ? Math.max(0, totalFreezesDuration - prev.totalFreezesDuration) : 0;

        const totalDeliveryDown = deltaPacketsLost + deltaPacketsRecv;
        const downLossPct = totalDeliveryDown > 0 ? (deltaPacketsLost / totalDeliveryDown) * 100 : 0;

        const audioDelivery = deltaAudioLost + deltaAudioRecv;
        const audioLossPct = audioDelivery > 0 ? (deltaAudioLost / audioDelivery) * 100 : 0;

        const videoDelivery = deltaVideoLost + deltaVideoRecv;
        const videoLossPct = videoDelivery > 0 ? (deltaVideoLost / videoDelivery) * 100 : 0;

        const upDelivery = deltaPacketsLostUp + deltaPacketsSent;
        const upLossPct = upDelivery > 0 ? (deltaPacketsLostUp / upDelivery) * 100 : 0;

        const outboundKbps = (direction === 'up' && dt > 0)
            ? Math.round((deltaBytesSent * 8) / dt) // bytes*8/ms = kbps
            : null;

        // bytesGrowing semantics: we want to declare "stalled" only if bytes have been
        // flat for the full `stallMs` window — NOT after a single zero-delta sample
        // (legitimate during a brief track-mute, a paused screenshare, or just sampling
        // hitting a quiet gap in the SFU's forwarding). Track the timestamp of the first
        // flat sample per PC; reset on any byte growth.
        const deltaBytes = direction === 'down' ? deltaBytesReceived : deltaBytesSent;
        let flatSince = prev ? (prev.flatSince ?? null) : null;
        if (deltaBytes > 0) {
            flatSince = null;
        } else if (flatSince === null && prev) {
            flatSince = now;
        }
        // Only mark stalled if we've been flat for the full stallMs window.
        let bytesGrowing;
        if (!prev) {
            bytesGrowing = null;            // unknown — first sample
        } else if (flatSince === null) {
            bytesGrowing = true;             // bytes moving
        } else if ((now - flatSince) >= cfg.thresholds.stallMs) {
            bytesGrowing = false;            // truly stalled
        } else {
            bytesGrowing = true;             // briefly flat — don't alarm yet
        }

        // Persist for next delta.
        pcCache.set(cacheKey, {
            ts: now, packetsLost, packetsReceived, audioLost, audioRecv,
            videoLost, videoRecv, packetsLostUp, packetsSent,
            bytesReceived, bytesSent, totalFreezesDuration, flatSince,
        });

        return {
            direction,
            memberId,
            streamType,
            metrics: {
                rttMs,
                jitterMs,
                packetLossPct: direction === 'down'
                    ? round1(downLossPct)
                    : round1(upLossPct),
                audioLossPct:  round1(audioLossPct),
                videoLossPct:  round1(videoLossPct),
                outboundKbps,
                freezeRate: dt > 0 ? round1((deltaFreezesDurationMs / dt) * 100) : null, // % of window frozen
                bytesGrowing,
            },
            // For reason inference: ms of frozen video in this sample window.
            freezeDurationMs: deltaFreezesDurationMs,
            hadPrev: !!prev,
        };
    }

    return { start, stop, getSnapshot, getLocalSnapshot, getMemberSnapshot, signalOffline, signalOnline };
}

// ──────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────

function emptyMetrics() {
    return {
        rttMs: null, jitterMs: null, packetLossPct: null,
        audioLossPct: null, videoLossPct: null,
        outboundKbps: null, freezeRate: null, bytesGrowing: null,
    };
}

function makeEmptySnapshot() {
    return { level: 'good', reason: null, metrics: emptyMetrics(), sampledAt: 0 };
}

function round1(n) { return Math.round(n * 10) / 10; }

// Merge two per-PC samples into one worst-case aggregate for a member or for upstream.
function mergeWorst(a, b) {
    if (!a) return b;
    if (!b) return a;
    const pick = (x, y, cmp) => {
        if (x == null) return y;
        if (y == null) return x;
        return cmp(x, y) ? x : y;
    };
    const max = (x, y) => x >= y;
    const merged = {
        direction: a.direction, // expected same family — caller groups them
        memberId: a.memberId,
        streamType: a.streamType,
        metrics: {
            rttMs:         pick(a.metrics.rttMs,         b.metrics.rttMs,         max),
            jitterMs:      pick(a.metrics.jitterMs,      b.metrics.jitterMs,      max),
            packetLossPct: pick(a.metrics.packetLossPct, b.metrics.packetLossPct, max),
            audioLossPct:  pick(a.metrics.audioLossPct,  b.metrics.audioLossPct,  max),
            videoLossPct:  pick(a.metrics.videoLossPct,  b.metrics.videoLossPct,  max),
            outboundKbps:  pick(a.metrics.outboundKbps,  b.metrics.outboundKbps,  (x, y) => x <= y), // worst = lowest
            freezeRate:    pick(a.metrics.freezeRate,    b.metrics.freezeRate,    max),
            bytesGrowing:  (a.metrics.bytesGrowing === false || b.metrics.bytesGrowing === false)
                ? false
                : (a.metrics.bytesGrowing || b.metrics.bytesGrowing),
        },
        freezeDurationMs: (a.freezeDurationMs || 0) + (b.freezeDurationMs || 0),
        hadPrev: a.hadPrev || b.hadPrev,
    };
    return merged;
}

// Classify a downstream (per-remote-member) sample → { level, reason }.
function classifyDownstream(rec, T) {
    if (!rec.hadPrev) return { level: 'good', reason: null };
    const m = rec.metrics;

    // Stall: no bytes growing while we expect them to → 'lost'
    if (m.bytesGrowing === false) return { level: 'lost', reason: REASONS.STALLED };

    // Audio loss is most user-perceptible (it's the "Alice is breaking up" signal).
    if (m.audioLossPct != null) {
        if (m.audioLossPct >= T.packetLossPct.lost) return { level: 'lost', reason: REASONS.AUDIO_LOSS };
        if (m.audioLossPct >= T.packetLossPct.poor) return { level: 'poor', reason: REASONS.AUDIO_LOSS };
        if (m.audioLossPct >= T.packetLossPct.fair) return { level: 'fair', reason: REASONS.AUDIO_LOSS };
    }

    // Freezes — video was frozen for a full second or more in this window.
    if (rec.freezeDurationMs >= 1000) {
        if (rec.freezeDurationMs >= 2000) return { level: 'poor', reason: REASONS.VIDEO_FREEZE };
        return { level: 'fair', reason: REASONS.VIDEO_FREEZE };
    }

    if (m.videoLossPct != null) {
        if (m.videoLossPct >= T.packetLossPct.poor) return { level: 'poor', reason: REASONS.VIDEO_LOSS };
        if (m.videoLossPct >= T.packetLossPct.fair) return { level: 'fair', reason: REASONS.VIDEO_LOSS };
    }

    if (m.jitterMs != null) {
        if (m.jitterMs >= T.jitterMs.poor) return { level: 'poor', reason: REASONS.JITTER };
        if (m.jitterMs >= T.jitterMs.fair) return { level: 'fair', reason: REASONS.JITTER };
    }

    return { level: 'good', reason: REASONS.RECOVERED };
}

// Classify the overall (your-side) record. RTT dominates here because it's
// the strongest indicator of "your link is sick."
function classifyOverall(rec, T) {
    if (!rec.hadPrev) return { level: 'good', reason: null };
    const m = rec.metrics;

    if (m.rttMs != null) {
        if (m.rttMs >= T.rttMs.lost) return { level: 'lost', reason: REASONS.RTT };
        if (m.rttMs >= T.rttMs.poor) return { level: 'poor', reason: REASONS.RTT };
    }

    if (m.packetLossPct != null) {
        if (m.packetLossPct >= T.packetLossPct.lost) return { level: 'lost', reason: REASONS.UPSTREAM_LOSS };
        if (m.packetLossPct >= T.packetLossPct.poor) return { level: 'poor', reason: REASONS.UPSTREAM_LOSS };
    }

    if (m.rttMs != null && m.rttMs >= T.rttMs.fair) return { level: 'fair', reason: REASONS.RTT };
    if (m.packetLossPct != null && m.packetLossPct >= T.packetLossPct.fair) return { level: 'fair', reason: REASONS.UPSTREAM_LOSS };
    if (m.jitterMs != null && m.jitterMs >= T.jitterMs.fair) return { level: 'fair', reason: REASONS.JITTER };

    return { level: 'good', reason: REASONS.RECOVERED };
}

/**
 * Hysteresis: only adopt a level change after N consecutive same-direction samples.
 * Prevents flapping when metrics straddle a threshold.
 *
 * @returns {{ state, changed: boolean, prevLevel: string }}
 */
function applyHysteresis(current, candidate, degradeNeeded, recoverNeeded) {
    if (!current) {
        // First observation — adopt immediately so the embedder gets an initial event.
        return {
            state: { snapshot: candidate, pendingLevel: candidate.level, sameLevelStreak: 1 },
            changed: candidate.level !== 'good',
            prevLevel: null,
        };
    }
    const curLevel = current.snapshot.level;
    const candLevel = candidate.level;

    if (candLevel === curLevel) {
        return {
            state: {
                snapshot: { ...candidate, level: curLevel }, // refresh metrics but keep level
                pendingLevel: curLevel,
                sameLevelStreak: 0,
            },
            changed: false,
            prevLevel: curLevel,
        };
    }

    // Different level proposed. Confirm via streak count.
    const isDegrade = levelRank(candLevel) > levelRank(curLevel);
    const needed = isDegrade ? degradeNeeded : recoverNeeded;

    if (current.pendingLevel === candLevel) {
        const streak = current.sameLevelStreak + 1;
        if (streak >= needed) {
            return {
                state: { snapshot: candidate, pendingLevel: candLevel, sameLevelStreak: 0 },
                changed: true,
                prevLevel: curLevel,
            };
        }
        // Still climbing the streak — keep current level, refresh metrics.
        return {
            state: {
                snapshot: { ...candidate, level: curLevel },
                pendingLevel: candLevel,
                sameLevelStreak: streak,
            },
            changed: false,
            prevLevel: curLevel,
        };
    }

    // New pending level — start the streak.
    return {
        state: {
            snapshot: { ...candidate, level: curLevel },
            pendingLevel: candLevel,
            sameLevelStreak: 1,
        },
        changed: false,
        prevLevel: curLevel,
    };
}
